import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Link2, Calendar, ShieldCheck, Scale, Rocket, Sparkles, Layers, Lock, ArrowRight, Wand2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useParlays } from '@/hooks/usePicks'
import { ParlayCard } from '@/components/ParlayCard'
import { SameGameParlayCard } from '@/components/SameGameParlayCard'
import { EmptyState } from '@/components/EmptyState'
import { SkeletonList } from '@/components/LoadingSkeleton'
import { ErrorState } from '@/components/ErrorState'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import { ResponsibleNotice } from '@/components/ResponsibleNotice'
import { RunBadge } from '@/components/RunBadge'
import { PillTabs } from '@/components/PillTabs'
import { StrategyPerformanceCard } from '@/components/StrategyPerformanceCard'
import { useStrategyPerformance } from '@/hooks/useStrategies'

type ParlaysTab = 'conservador' | 'balanceado' | 'agresivo' | 'sgp'
// Sprint "Parlay Official Final" (25/09/2026): etiquetas renombradas a JA Duo/Triple/Quad --
// las claves internas ('conservador'/'balanceado'/'agresivo') no cambian (continuidad con
// `parlay_type`/`StrategyPerformance`, ver `ParlayCard.tsx`).
const STANDARD_TABS: { key: ParlaysTab; label: string }[] = [
  { key: 'conservador', label: 'JA Duo' },
  { key: 'balanceado', label: 'JA Triple' },
  { key: 'agresivo', label: 'JA Quad' },
]

function getTodayIso(): string {
  return new Date().toISOString().split('T')[0]
}

/** P0.23 (22/08/2026, Sección 7/9 del encargo) -- las 3 estrategias normales (Conservador/
 * Balanceado/Agresivo) se identifican por `parlay_type` REAL, nunca por número de piernas
 * (bug real de la versión anterior: `PARLAY_CONFIG[legCount]` en `ParlayCard.tsx` no cubría
 * Same Game Parlay con 4-7 piernas, así que si un SGP llegaba a esa card mostraba un título
 * crudo `Parlay same_game_parlay`). Same Game Parlay se separa visualmente por completo
 * (Sección 9: "NO mezclarlo visualmente con los tres Parlays normales") en vez de ordenarse
 * junto a los demás por cantidad de selecciones como hacía la versión anterior.
 */
/** Sprint "Parlay V2 BLACK: Personalized Parlay Lab" (05/09/2026, Sección 6 del encargo):
 * teaser de descubrimiento -- MISMO patrón que `JALabTeaser` (`HomePage.tsx`): BLACK/ADMIN
 * ven la invitación real a construir su propio parlay; FREE/PRO ven el mismo espacio,
 * visiblemente locked, con CTA a BLACK. */
function ParlayLabTeaser({ plan, role }: { plan: string; role?: string }) {
  // "Corrección final Sprint 16" (05/09/2026, hallazgo 3): una cuenta ADMIN cuyo `plan`
  // todavía no sea "ADMIN"/"BLACK" (rol y plan son señales independientes, ver `plan_
  // tiers.py`) nunca debe ver este teaser como bloqueado -- MISMO criterio real que ya
  // exige el backend (`require_parlay_lab_access`) y la propia página (`ParlayLabPage`).
  const isBlack = plan === 'BLACK' || plan === 'ADMIN' || role === 'ADMIN'
  return (
    <Link
      to={isBlack ? '/parlay-lab' : '/mas/suscripcion'}
      className="card p-4 rounded-2xl space-y-2 border-exact/25 block hover:border-exact/40 transition"
    >
      <div className="flex items-center gap-1.5 eyebrow text-exact">
        {isBlack ? <Layers className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />} Parlay Lab
      </div>
      <p className="text-sm font-semibold text-text">
        {isBlack ? '¿Quieres combinar tus propios partidos?' : 'Combina tus propios partidos'}
      </p>
      <p className="text-xs text-text-muted leading-relaxed">
        {isBlack
          ? 'Elige los partidos, las familias de mercado y tu perfil de riesgo -- John Anthony construye el mejor parlay defendible.'
          : 'Exclusivo BLACK.'}
      </p>
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-exact pt-1">
        {isBlack ? 'Construir mi parlay' : 'Descubrir BLACK'} <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  )
}

/** Sprint "SGP V2 BLACK: Personalized SGP Lab" (05/09/2026, Sección 8 del encargo): mismo
 * patrón que `ParlayLabTeaser` -- BLACK/ADMIN ven la invitación real a construir su propio
 * Same Game Parlay; FREE/PRO ven el mismo espacio, visiblemente locked, con CTA a BLACK.
 * Solo Big 5 + UEFA Champions League (Sección 1 del encargo), ya filtrado por el backend. */
function SGPLabTeaser({ plan, role }: { plan: string; role?: string }) {
  // "Corrección final Sprint 16" (05/09/2026, hallazgo 3): mismo criterio que
  // `ParlayLabTeaser` -- ADMIN por rol nunca debe verse bloqueado aunque su `plan` todavía
  // no sea "ADMIN"/"BLACK".
  const isBlack = plan === 'BLACK' || plan === 'ADMIN' || role === 'ADMIN'
  return (
    <Link
      to={isBlack ? '/sgp-lab' : '/mas/suscripcion'}
      className="card p-4 rounded-2xl space-y-2 border-violet/25 block hover:border-violet/40 transition"
    >
      <div className="flex items-center gap-1.5 eyebrow text-violet">
        {isBlack ? <Wand2 className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />} SGP Lab
      </div>
      <p className="text-sm font-semibold text-text">
        {isBlack ? '¿Quieres construir tu propio Same Game Parlay?' : 'Construye tu propio Same Game Parlay'}
      </p>
      <p className="text-xs text-text-dim leading-relaxed">
        {isBlack
          ? 'Elige un partido de Big 5/Champions, las familias que te interesan y tu rango de cuota -- John Anthony construye el SGP más defendible.'
          : 'Exclusivo BLACK.'}
      </p>
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-violet pt-1">
        {isBlack ? 'Construir mi SGP' : 'Descubrir BLACK'} <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  )
}

export function ParlaysPage() {
  const { user } = useAuth()
  const [selectedDate, setSelectedDate] = useState(getTodayIso())
  const isToday = selectedDate === getTodayIso()
  const [searchParams] = useSearchParams()
  const runParam = searchParams.get('run')
  const runType = runParam === 'run1' || runParam === 'run2' ? runParam : undefined

  const { data, isLoading, isError, error, refetch } = useParlays(true, isToday ? undefined : selectedDate, runType)

  const perfConservador = useStrategyPerformance('parlay_conservador')
  const perfBalanceado = useStrategyPerformance('parlay_balanceado')
  const perfAgresivo = useStrategyPerformance('parlay_agresivo')
  // P0.26 (Sección 6 del encargo): mismo hook genérico, ya funciona -- el backend reutiliza
  // `compute_parlay_category_performance` sin lógica nueva (filtra por `parlay_type` real).
  const perfSGP = useStrategyPerformance('parlay_same_game_parlay')

  const allParlays = data?.parlays ?? []
  const sgpParlays = allParlays.filter((p) => p.parlay_type === 'same_game_parlay')
  const parlaysByType = useMemo(() => {
    const grouped: Record<string, typeof allParlays> = { conservador: [], balanceado: [], agresivo: [] }
    for (const p of allParlays) {
      if (p.parlay_type && p.parlay_type in grouped) grouped[p.parlay_type].push(p)
    }
    return grouped
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allParlays])

  // Sprint "Product Experience V2" (10/09/2026, mockup aprobado): Conservador/Balanceado/
  // Agresivo/SGP pasan de 4 secciones apiladas a pestañas -- combinado completo legible sin
  // desplazarse por las 4 estrategias a la vez. SGP se mantiene "NUNCA mezclado visualmente"
  // con las 3 normales (Sección 9 del encargo original) -- sigue siendo su propia pestaña,
  // nunca una card más dentro de la lista de Conservador/Balanceado/Agresivo.
  const [tab, setTab] = useState<ParlaysTab>('conservador')
  const perfByTab: Record<ParlaysTab, typeof perfConservador> = {
    conservador: perfConservador, balanceado: perfBalanceado, agresivo: perfAgresivo, sgp: perfSGP,
  }
  const iconByTab: Record<ParlaysTab, typeof ShieldCheck> = {
    conservador: ShieldCheck, balanceado: Scale, agresivo: Rocket, sgp: Sparkles,
  }
  const colorByTab: Record<ParlaysTab, string> = {
    conservador: 'green', balanceado: 'blue', agresivo: 'red', sgp: 'violet',
  }

  return (
    <div className="p-4 space-y-4">
      {runType && <RunBadge runType={runType} />}

      <div className="flex items-center justify-between bg-panel-elevated/40 p-3 rounded-2xl border border-border/40">
        <div>
          <h2 className="text-xs font-black uppercase text-text tracking-wider">Combinadas Oficiales</h2>
          <p className="text-[10px] text-text-dim font-bold">Selecciones de alto valor</p>
        </div>

        <div className="flex items-center gap-1.5 bg-panel p-1.5 rounded-xl border border-border">
          <Calendar className="h-3.5 w-3.5 text-text-dim" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-transparent text-xs font-mono font-bold text-text focus:outline-none"
          />
        </div>
      </div>

      {/* "Corrección final Sprint 16" (05/09/2026, hallazgo 3): Parlay Lab/SGP Lab deben ser
          DESCUBRIBLES desde /parlays sin ninguna interacción extra -- ambos teasers viven
          SIEMPRE visibles aquí, nunca detrás de una pestaña (a diferencia de las
          combinaciones ya generadas, que sí viven dentro de su pestaña). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ParlayLabTeaser plan={user?.plan ?? 'FREE'} role={user?.role} />
        <SGPLabTeaser plan={user?.plan ?? 'FREE'} role={user?.role} />
      </div>

      {isLoading && <SkeletonList count={2} />}
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      {data?.locked && (
        // P0.53 (29/08/2026, Sección 6 del encargo): fallback defensivo corregido -- ELITE
        // nunca es un plan real que un cliente pueda contratar; PRO es el nivel mínimo real
        // que desbloquea Parlays (`plan_tiers.py`).
        <UpgradePrompt requiredPlan={data.required_plan ?? 'PRO'} feature="Parlays" trialExpired={data.trial_expired} />
      )}

      {data && !data.locked && allParlays.length === 0 && (
        <EmptyState
          icon={Link2}
          title="Sin parlays registrados en esta fecha"
          body="Ninguna combinación cumplió los criterios de correlación para la fecha seleccionada."
        />
      )}

      {data && !data.locked && allParlays.length > 0 && (
        <>
          <PillTabs
            items={[
              ...STANDARD_TABS.map((t) => ({ ...t, count: parlaysByType[t.key].length })),
              { key: 'sgp', label: 'SGP', count: sgpParlays.length },
            ]}
            active={tab}
            onChange={(k) => setTab(k as ParlaysTab)}
          />

          {tab !== 'sgp' ? (
            <section className="space-y-3">
              {parlaysByType[tab].length > 0 ? (
                parlaysByType[tab].map((parlay) => <ParlayCard key={parlay.id} parlay={parlay} />)
              ) : (
                <EmptyState icon={Link2} title="Sin parlay en esta categoría hoy" body="Ninguna combinación cumplió los criterios para esta estrategia hoy." />
              )}
            </section>
          ) : (
            /* Sección 9 original: Same Game Parlay -- "Un partido. Múltiples mercados." --
               reutiliza el SGP Intelligence Engine real, nunca lenguaje genérico de valor. */
            <section className="space-y-3">
              {sgpParlays.length > 0 ? (
                sgpParlays.map((parlay) => <SameGameParlayCard key={parlay.id} parlay={parlay} />)
              ) : (
                !isLoading && (
                  <div className="card p-5 text-center space-y-1.5 border-violet/20">
                    <p className="text-xs font-bold text-text">No hay Same Game Parlay disponible hoy.</p>
                    <p className="text-[11px] text-text-dim">Solo se genera cuando existe un partido con información suficiente en las ligas cubiertas.</p>
                  </div>
                )
              )}
            </section>
          )}

          {/* P0.56 (Sección 8 del encargo): el rendimiento histórico va DESPUÉS de los
              parlays de hoy, nunca antes -- ya no protagoniza la pantalla, y ahora vive
              dentro de la pestaña activa (mismo dato/componente de siempre). */}
          <section className="space-y-2 pt-1">
            <p className="eyebrow px-1">Rendimiento histórico</p>
            <StrategyPerformanceCard
              icon={iconByTab[tab]}
              label={tab === 'sgp' ? 'Same Game Parlay' : `Parlay ${STANDARD_TABS.find((t) => t.key === tab)?.label}`}
              color={colorByTab[tab]}
              stats={perfByTab[tab].data}
            />
          </section>
        </>
      )}

      {allParlays.length > 0 && <ResponsibleNotice />}
    </div>
  )
}
